/**
 * File Service
 * 
 * Frontend service for file management operations.
 * Handles uploads, downloads, and file organization with strict
 * workspace separation between personal and organization files.
 */

import api from './api';

const FILE_API = '/files';

/**
 * File upload with progress tracking
 * Uses presigned URLs for direct S3 upload
 */
class FileService {
  
  // ============================================
  // PERSONAL WORKSPACE OPERATIONS
  // ============================================
  
  /**
   * Request upload URL for personal workspace file
   * @param {Object} fileInfo - File information
   * @returns {Promise<Object>} - Upload URL and file ID
   */
  async requestPersonalUploadUrl(fileInfo) {
    const response = await api.post(`${FILE_API}/personal/upload-url`, fileInfo);
    return response.data;
  }
  
  /**
   * Upload file to personal workspace
   * @param {File} file - File object
   * @param {Object} options - Upload options
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<Object>} - Uploaded file data
   */
  async uploadPersonalFile(file, options = {}, onProgress = null) {
    const { folder = '/', context = { type: 'general' }, metadata = {} } = options;
    
    // Step 1: Get presigned upload URL
    const urlResponse = await this.requestPersonalUploadUrl({
      filename: file.name,
      mimeType: file.type,
      size: file.size,
      folder,
      context,
      metadata
    });
    
    if (!urlResponse.success) {
      throw new Error(urlResponse.message || 'Failed to get upload URL');
    }
    
    const { uploadUrl, fileId } = urlResponse.data;
    
    // Step 2: Upload directly to S3 using presigned URL
    await this.uploadToS3(uploadUrl, file, file.type, onProgress);
    
    // Step 3: Confirm upload completion
    const confirmResponse = await this.confirmUpload(fileId);
    
    return confirmResponse.data;
  }
  
  /**
   * List personal workspace files
   * @param {Object} options - Query options
   * @returns {Promise<Object>} - Files and pagination
   */
  async listPersonalFiles(options = {}) {
    const params = new URLSearchParams();
    if (options.folder) params.append('folder', options.folder);
    if (options.contextType) params.append('contextType', options.contextType);
    if (options.search) params.append('search', options.search);
    if (options.page) params.append('page', options.page);
    if (options.limit) params.append('limit', options.limit);
    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortOrder) params.append('sortOrder', options.sortOrder);
    
    const response = await api.get(`${FILE_API}/personal?${params.toString()}`);
    return response.data;
  }
  
  /**
   * Get personal workspace folder structure
   * @returns {Promise<Array>} - Folder list
   */
  async getPersonalFolders() {
    const response = await api.get(`${FILE_API}/folders/personal`);
    return response.data;
  }
  
  /**
   * Get personal storage usage
   * @returns {Promise<Object>} - Storage usage info
   */
  async getPersonalStorageUsage() {
    const response = await api.get(`${FILE_API}/storage/personal`);
    return response.data;
  }
  
  // ============================================
  // ORGANIZATION WORKSPACE OPERATIONS
  // ============================================
  
  /**
   * Request upload URL for organization workspace file
   * @param {string} organizationId - Organization ID
   * @param {Object} fileInfo - File information
   * @returns {Promise<Object>} - Upload URL and file ID
   */
  async requestOrganizationUploadUrl(organizationId, fileInfo) {
    const response = await api.post(
      `${FILE_API}/organization/${organizationId}/upload-url`,
      fileInfo
    );
    return response.data;
  }
  
  /**
   * Upload file to organization workspace
   * @param {string} organizationId - Organization ID
   * @param {File} file - File object
   * @param {Object} options - Upload options
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<Object>} - Uploaded file data
   */
  async uploadOrganizationFile(organizationId, file, options = {}, onProgress = null) {
    const { 
      folder = '/', 
      context = { type: 'general' }, 
      visibility = 'private',
      metadata = {} 
    } = options;
    
    // Step 1: Get presigned upload URL
    const urlResponse = await this.requestOrganizationUploadUrl(organizationId, {
      filename: file.name,
      mimeType: file.type,
      size: file.size,
      folder,
      context,
      visibility,
      metadata
    });
    
    if (!urlResponse.success) {
      throw new Error(urlResponse.message || 'Failed to get upload URL');
    }
    
    const { uploadUrl, fileId } = urlResponse.data;
    
    // Step 2: Upload directly to S3
    await this.uploadToS3(uploadUrl, file, file.type, onProgress);
    
    // Step 3: Confirm upload
    const confirmResponse = await this.confirmUpload(fileId);
    
    return confirmResponse.data;
  }
  
  /**
   * List organization workspace files
   * @param {string} organizationId - Organization ID
   * @param {Object} options - Query options
   * @returns {Promise<Object>} - Files and pagination
   */
  async listOrganizationFiles(organizationId, options = {}) {
    const params = new URLSearchParams();
    if (options.folder) params.append('folder', options.folder);
    if (options.projectId) params.append('projectId', options.projectId);
    if (options.contextType) params.append('contextType', options.contextType);
    if (options.search) params.append('search', options.search);
    if (options.uploadedBy) params.append('uploadedBy', options.uploadedBy);
    if (options.page) params.append('page', options.page);
    if (options.limit) params.append('limit', options.limit);
    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortOrder) params.append('sortOrder', options.sortOrder);
    
    const response = await api.get(
      `${FILE_API}/organization/${organizationId}?${params.toString()}`
    );
    return response.data;
  }
  
  /**
   * Get organization folder structure
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Array>} - Folder list
   */
  async getOrganizationFolders(organizationId) {
    const response = await api.get(`${FILE_API}/folders/organization/${organizationId}`);
    return response.data;
  }
  
  /**
   * Get organization storage usage
   * @param {string} organizationId - Organization ID
   * @param {boolean} detailed - Whether to get detailed breakdown
   * @returns {Promise<Object>} - Storage usage info
   */
  async getOrganizationStorageUsage(organizationId, detailed = false) {
    const params = detailed ? '?detailed=true' : '';
    const response = await api.get(`${FILE_API}/storage/organization/${organizationId}${params}`);
    return response.data;
  }
  
  // ============================================
  // CONTEXT-BASED OPERATIONS
  // ============================================
  
  /**
   * Get files for a project
   * @param {string} projectId - Project ID
   * @returns {Promise<Array>} - Project files
   */
  async getProjectFiles(projectId) {
    const response = await api.get(`${FILE_API}/project/${projectId}`);
    return response.data;
  }
  
  /**
   * Get files for a task
   * @param {string} taskId - Task ID
   * @returns {Promise<Array>} - Task files
   */
  async getTaskFiles(taskId) {
    const response = await api.get(`${FILE_API}/task/${taskId}?t=${Date.now()}`);
    return response.data;
  }
  
  /**
   * Upload file attached to a task
   * @param {string} taskId - Task ID
   * @param {File} file - File object
   * @param {string} workspaceType - 'personal' or 'organization'
   * @param {string} organizationId - Organization ID (for org workspace)
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<Object>} - Uploaded file
   */
  async uploadTaskAttachment(taskId, file, workspaceType, organizationId = null, onProgress = null) {
    const context = { type: 'task', taskId };
    
    if (workspaceType === 'personal') {
      return this.uploadPersonalFile(file, { context }, onProgress);
    } else {
      return this.uploadOrganizationFile(organizationId, file, { context }, onProgress);
    }
  }
  
  /**
   * Upload file attached to a project
   * @param {string} projectId - Project ID
   * @param {File} file - File object
   * @param {string} organizationId - Organization ID
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<Object>} - Uploaded file
   */
  async uploadProjectFile(projectId, file, organizationId, onProgress = null) {
    const context = { type: 'project', projectId };
    return this.uploadOrganizationFile(organizationId, file, { context }, onProgress);
  }
  
  // ============================================
  // INDIVIDUAL FILE OPERATIONS
  // ============================================
  
  /**
   * Confirm file upload completion
   * @param {string} fileId - File ID
   * @returns {Promise<Object>} - Confirmed file
   */
  async confirmUpload(fileId) {
    const response = await api.post(`${FILE_API}/${fileId}/confirm`);
    return response.data;
  }
  
  /**
   * Get file download URL
   * @param {string} fileId - File ID
   * @returns {Promise<Object>} - Download URL info
   */
  async getDownloadUrl(fileId) {
    const response = await api.get(`${FILE_API}/${fileId}/download`);
    return response.data;
  }
  
  /**
   * Download file
   * @param {string} fileId - File ID
   * @param {string} filename - Filename for download
   */
  async downloadFile(fileId, filename = null) {
    const response = await this.getDownloadUrl(fileId);
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to get download URL');
    }
    
    const { downloadUrl, filename: originalFilename } = response.data;
    
    // Create temporary link and trigger download
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename || originalFilename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  
  /**
   * Get file details
   * @param {string} fileId - File ID
   * @returns {Promise<Object>} - File details
   */
  async getFileDetails(fileId) {
    const response = await api.get(`${FILE_API}/${fileId}`);
    return response.data;
  }
  
  /**
   * Update file metadata
   * @param {string} fileId - File ID
   * @param {Object} updates - Metadata updates
   * @returns {Promise<Object>} - Updated file
   */
  async updateFile(fileId, updates) {
    const response = await api.patch(`${FILE_API}/${fileId}`, updates);
    return response.data;
  }
  
  /**
   * Delete file
   * @param {string} fileId - File ID
   * @param {boolean} permanent - Whether to permanently delete
   * @returns {Promise<Object>} - Result
   */
  async deleteFile(fileId, permanent = false) {
    const response = await api.delete(
      `${FILE_API}/${fileId}${permanent ? '?permanent=true' : ''}`
    );
    return response.data;
  }
  
  /**
   * Bulk delete files
   * @param {Array<string>} fileIds - File IDs to delete
   * @param {boolean} permanent - Whether to permanently delete
   * @returns {Promise<Object>} - Results
   */
  async bulkDeleteFiles(fileIds, permanent = false) {
    const response = await api.post(`${FILE_API}/bulk-delete`, { fileIds, permanent });
    return response.data;
  }
  
  /**
   * Move file to different folder
   * @param {string} fileId - File ID
   * @param {string} folder - Target folder path
   * @returns {Promise<Object>} - Updated file
   */
  async moveFile(fileId, folder) {
    const response = await api.patch(`${FILE_API}/${fileId}/move`, { folder });
    return response.data;
  }
  
  /**
   * Copy file
   * @param {string} fileId - File ID
   * @param {string} folder - Target folder path
   * @returns {Promise<Object>} - New file
   */
  async copyFile(fileId, folder = '/') {
    const response = await api.post(`${FILE_API}/${fileId}/copy`, { folder });
    return response.data;
  }
  
  /**
   * Share file with user
   * @param {string} fileId - File ID
   * @param {string} userId - User ID to share with
   * @param {string} permission - Permission level ('view', 'edit', 'admin')
   * @returns {Promise<Object>} - Updated file
   */
  async shareFile(fileId, userId, permission = 'view') {
    const response = await api.post(`${FILE_API}/${fileId}/share`, { userId, permission });
    return response.data;
  }
  
  /**
   * Remove file sharing
   * @param {string} fileId - File ID
   * @param {string} userId - User ID to unshare with
   * @returns {Promise<Object>} - Updated file
   */
  async unshareFile(fileId, userId) {
    const response = await api.delete(`${FILE_API}/${fileId}/share/${userId}`);
    return response.data;
  }
  
  // ============================================
  // SEARCH
  // ============================================
  
  /**
   * Search files
   * @param {string} query - Search query
   * @param {Object} options - Search options
   * @returns {Promise<Array>} - Matching files
   */
  async searchFiles(query, options = {}) {
    const params = new URLSearchParams();
    params.append('q', query);
    if (options.workspaceType) params.append('workspaceType', options.workspaceType);
    if (options.mimeTypes) params.append('mimeTypes', options.mimeTypes.join(','));
    if (options.dateFrom) params.append('dateFrom', options.dateFrom);
    if (options.dateTo) params.append('dateTo', options.dateTo);
    if (options.limit) params.append('limit', options.limit);
    
    const response = await api.get(`${FILE_API}/search?${params.toString()}`);
    return response.data;
  }
  
  // ============================================
  // UTILITY METHODS
  // ============================================
  
  /**
   * Get allowed file types
   * @returns {Promise<Object>} - Allowed MIME types
   */
  async getAllowedFileTypes() {
    const response = await api.get(`${FILE_API}/allowed-types`);
    return response.data;
  }
  
  /**
   * Upload file directly to S3 using presigned URL
   * @param {string} uploadUrl - Presigned S3 URL
   * @param {File} file - File object
   * @param {string} contentType - Content type
   * @param {Function} onProgress - Progress callback
   */
  async uploadToS3(uploadUrl, file, contentType, onProgress = null) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          onProgress(percentComplete);
        }
      });
      
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });
      
      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });
      
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.send(file);
    });
  }
  
  /**
   * Format file size for display
   * @param {number} bytes - File size in bytes
   * @returns {string} - Formatted size
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
  
  /**
   * Get file icon based on MIME type
   * @param {string} mimeType - File MIME type
   * @returns {string} - Icon name/class
   */
  getFileIcon(mimeType) {
    if (!mimeType) return 'file';
    
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';
    if (mimeType === 'application/pdf') return 'file-pdf';
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return 'file-spreadsheet';
    if (mimeType.includes('document') || mimeType.includes('word')) return 'file-text';
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return 'file-presentation';
    if (mimeType.includes('zip') || mimeType.includes('archive') || mimeType.includes('compressed')) return 'file-archive';
    if (mimeType.startsWith('text/')) return 'file-code';
    
    return 'file';
  }
  
  /**
   * Check if file type is allowed
   * @param {string} mimeType - File MIME type
   * @param {Array} allowedTypes - List of allowed types
   * @returns {boolean} - Whether type is allowed
   */
  isAllowedType(mimeType, allowedTypes) {
    return allowedTypes.includes(mimeType);
  }
  
  /**
   * Check if file is previewable in browser
   * @param {string} mimeType - File MIME type
   * @returns {boolean} - Whether file can be previewed
   */
  isPreviewable(mimeType) {
    const previewableTypes = [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'image/svg+xml',
      'application/pdf',
      'text/plain',
      'text/html',
      'video/mp4',
      'video/webm',
      'audio/mpeg',
      'audio/wav'
    ];
    return previewableTypes.includes(mimeType);
  }
}

// Export singleton instance
const fileService = new FileService();
export default fileService;

// Also export individual methods for convenience
export const {
  uploadPersonalFile,
  uploadOrganizationFile,
  listPersonalFiles,
  listOrganizationFiles,
  getProjectFiles,
  getTaskFiles,
  getDownloadUrl,
  downloadFile,
  deleteFile,
  moveFile,
  shareFile,
  searchFiles,
  formatFileSize,
  getFileIcon
} = fileService;
